/**
 * CreateInsuranceConfirm — the proof-read, one moment before the policy exists.
 *
 * `CreateAdvanceConfirm`'S SURFACE OVER A YEAR OF COVER, and deliberately its shape down to the
 * class names: every particular the instrument states, in the instrument's own order and under the
 * instrument's own row labels, as it will print. NOT "are you sure?" — the last chance anybody has
 * to notice that the weekly recovery is a decimal point out, or that the premium picked is the wrong
 * GH₵748 line, is the moment before an `insurances` row exists and the figure is in `total_value`.
 *
 * AND THERE ARE TWO INSTRUMENTS, WHICH IS THE WHOLE DIFFERENCE FROM THE ADVANCE'S [D-B45]. A year
 * Primelane financed prints the Insurance Premium Advance's money grid; a year the driver paid for
 * in full prints the Insurance Premium Payment Record's, which advances nothing and charges nothing.
 * Which of the two is `snapshot.instrument` — the DRAFT's shape, decided by `insuranceInstrument`
 * through `instrumentFor` and carried here rather than asked again.
 *
 * THE WORDS COME OUT OF A PURE FUNCTION (`insuranceSummary`), so what this dialog asserts is
 * testable without a browser and cannot drift from what the writer is about to send — nor from the
 * Advance canvas behind it, which draws the same clauses from the same call.
 *
 * MOUNTED ONCE, BY THE PAGE. It is a Radix `AlertDialog` portalled to `document.body`, exactly as
 * `ActivationConfirm` is — which is also why it is not rendered from inside the commit bar: the bar
 * is drawn on the Advance canvas and a dialog mounted from a control that can unmount underneath it
 * is a dialog that closes itself mid-confirmation.
 */

import { Loader2 } from 'lucide-react';

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { INSTRUMENT_TITLES } from '@/lib/agreements/instrument';
import { cn } from '@/lib/utils';

import { BRAND_BUTTON } from '../../manifest/NarratorLine';
import type { InsuranceDraft } from '../insuranceForm';
import type { InsuranceSnapshot } from '../insuranceState';
import { insuranceSummary } from '../insuranceSummary';

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    draft: InsuranceDraft;
    snapshot: InsuranceSnapshot;
    /**
     * What the driver will have paid towards this premium once the save lands.
     *
     * PASSED IN RATHER THAN SUMMED HERE. `draftDepositTotal` is its one author, it reads the FORM's
     * strings, and it carries the deposit rows this flow does not edit — a second sum taken off the
     * parsed draft alone would print an Advance's principal over a premium the driver has settled,
     * on the one surface that exists to be read against the document.
     */
    depositTotal: number;
    pending: boolean;
    /** Refused by `validateDraft` or by the certificate row — the bar that opens this is disabled. */
    blocked: boolean;
    onConfirm: () => void;
}

export default function CreateInsuranceConfirm({
    open,
    onOpenChange,
    draft,
    snapshot,
    depositTotal,
    pending,
    blocked,
    onConfirm,
}: Props) {
    const summary = insuranceSummary(draft, snapshot, depositTotal);
    const title = INSTRUMENT_TITLES[snapshot.instrument];

    return (
        <AlertDialog open={open} onOpenChange={onOpenChange}>
            <AlertDialogContent className="sd-tokens max-h-[85vh] max-w-2xl overflow-y-auto">
                <AlertDialogHeader>
                    <AlertDialogTitle>Create this policy?</AlertDialogTitle>
                    <AlertDialogDescription>
                        This records the year of cover, the payment made to the insurer and the
                        premium the contract carries, together. Read it back — this is what the{' '}
                        {title} prints.
                    </AlertDialogDescription>
                </AlertDialogHeader>

                <div className="sd-clauses">
                    {summary.clauses.map((clause) => (
                        <div className="sd-clause" key={clause.no}>
                            <span className="sd-cl-no">{clause.no}</span>
                            <p className="sd-cl-text">
                                {clause.parts.map((piece, index) => {
                                    if (piece.kind === 'text') return <span key={index}>{piece.text}</span>;
                                    return (
                                        <span
                                            key={index}
                                            className={cn(
                                                'sd-cl-val',
                                                piece.kind === 'missing' && 'sd-cl-val-missing',
                                            )}
                                        >
                                            {piece.text}
                                        </span>
                                    );
                                })}
                            </p>
                        </div>
                    ))}
                </div>

                {/* THE PARTIES ARE ON THE PROOF-READ AND NOT EDITABLE FROM IT. They are
                    render-blocking merge keys fixed on the contract's own areas, and the block on
                    the Agreement canvas links out to each — what belongs here is only the reading. */}
                <div>
                    <p className="sd-hint">{summary.heading}</p>
                    <div className="sd-rows">
                        {summary.facts.map((fact) => (
                            <div className="sd-rec" key={fact.field}>
                                <div className="sd-rec-k">{fact.label}</div>
                                <div className={cn('sd-rec-v sd-num', !fact.value && 'sd-empty')}>
                                    {fact.value ?? '—'}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {summary.missing.length > 0 && (
                    <p className="sd-hint text-[hsl(var(--destructive))]">
                        {summary.missing.length === 1
                            ? '1 field this policy requires is empty: '
                            : `${summary.missing.length} fields this policy requires are empty: `}
                        {summary.missing.map((fact) => fact.label.toLowerCase()).join(', ')}.
                    </p>
                )}

                <AlertDialogFooter>
                    <AlertDialogCancel disabled={pending}>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                        className={BRAND_BUTTON}
                        onClick={(event) => {
                            // The dialog would close itself on click; the write decides when it
                            // closes, so a refusal does not leave the admin staring at a dismissed
                            // surface with no explanation — and a create that lands mid-sequence
                            // closes it from `onIdentity`, where the policy starts existing.
                            event.preventDefault();
                            onConfirm();
                        }}
                        disabled={pending || blocked}
                    >
                        {pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Create policy
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
